/**
 * AgentEnquirySimple
 * 
 * @Author: Focci
 * @Date: 2024-04-25 15:19:06
 * @Last Modified by: Focci
 * @Last Modified time: 2024-04-25 15:19:06
 */

import SVGEmail from '@img/email.svg'
import useTracking from '@hook/useTracking'
import useLogin from '@hook/useLogin'
import dynamic from 'next/dynamic'
import clsx from 'clsx'
// import StyledInput from '@comp/form/StyledInput'
import { useTranslations } from 'next-intl'
import { Button } from '@comp/material'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useEnquiryInfo } from '@hook/useEnquiryInfo'
import { clean, getPhonePrefix, houseToGta, isAU } from '@lib/utils'
import { isArray, isFunction, map } from 'lodash-es'

/**
 * 如果这里换成直接引入的话，会报下面的错误
 * Warning: A component is changing an uncontrolled input to be controlled. 
 *   This is likely caused by the value changing from undefined to a defined value, 
 *   which should not happen. Decide between using a controlled or uncontrolled 
 *   input element for the lifetime of the component. 
 *   More info: https://reactjs.org/link/controlled-components
 */
const StyledInput = dynamic(() => import('@comp/form/StyledInput'), { ssr: false })

export function useAgentEnquirySimple({
  house,
  beforeSubmit
}) {
  const { user } = useLogin()
  const { pushTrackListingClick, pushTrack, pushTrackListing } = useTracking()

  const { 
    loading, userEmail, userName, userMobile, emailRules, submitEnquiry 
  } = useEnquiryInfo()

  const [email, setEmail] = useState()
  useEffect(() => setEmail(userEmail), [userEmail])

  const disabledButton = useMemo(() => (
    !emailRules.pattern.value.test(email) || loading
  ), [email, emailRules, loading])

  const inputProps = useMemo(() => ({
    value: email,
    onChange: (e) => setEmail(e.target.value),
  }), [email])

  const handleConfirm = useCallback(() => {
    // 添加事件统计
    pushTrackListingClick('estimate-email', house)
    
    // 提交enquiry
    const { id, agents, code } = house
    const agentId = map(isArray(agents) ? agents : [], 'id')[0]

    // https://7thave.atlassian.net/browse/HT-3273
    // 发给所有中介，后端反馈不用传 agentId
    const tmp = { 
      email,
      id, 
      name: userName || user?.nickname,
      phoneNumber: userMobile || user?.mobile_number,
      type: 'house',
      body: `I am interested in this property ref:${code}, and I would like What is the price range, please send me more information`, 
      spec: 'price', 
      countryCode: getPhonePrefix(),
    }
    
    const param = clean(
      isFunction(beforeSubmit) ? beforeSubmit(tmp, house) : tmp
    )

    pushTrack('click_email_sent', { houseId: id, agentId, agencyOfficeId: house?.office?.id })
    pushTrackListing('click_email_sent', house)
    isAU && pushTrack('homely_email_enquiry', houseToGta(house))
    
    submitEnquiry(param)
  }, [
    pushTrackListingClick, submitEnquiry, pushTrack, pushTrackListing, beforeSubmit,
    email, house, user, userMobile, userName
  ])

  return {
    email,
    emailRules,
    disabledButton,
    loading,
    inputProps,
    handleConfirm,
    setEmail,
  }
}

export function AgentEnquirySimpleInput({ className, ...props }) {
  return (
    <div className={clsx(['flex relative', className])}>
      <div className="absolute left-3 top-1/2 -translate-y-1/2 z-1 pointer-events-none">
        <SVGEmail />
      </div>
      <StyledInput 
        variant="outline" 
        className="pl-9"
        {...props}
        // placeholder={te('typeYourEmail')}
        // value={email}
        // onChange={(e) => setEmail(e.target.value)}
      />
    </div>
  )
}

export function AgentEnquirySimpleButton({ children, ...props }) {
  return (
    <Button
      size="large"
      color="error"
      // onClick={handleConfirm}
      // disabled={loading || disabledButton}
      className="w-full leading-[1.94]"
      {...props}
    >
      {children}
    </Button>
  )
}

export default function AgentEnquirySimple({ house, inline }) {
  const te = useTranslations('enquiry')
  const th = useTranslations('house')
  
  const { inputProps, disabledButton, loading, handleConfirm } = useAgentEnquirySimple({ house })

  return (
    <div
      className={clsx([
        'flex flex-col gap-4 border p-4 rounded-sm min-h-auto',
        !inline && 'md:min-h-[13rem]'
      ])}
    >
      {!house.avm && (
        <div className="text-center text-helper -mb-2">
          {th('noAvmHelper')}
        </div>
      )}
      <div className="text-base font-medium text-center">
        {te('getPriceOfProperty')}
      </div>
      <div className={clsx(['flex gap-4', inline ? 'flex-row' : 'flex-col'])}>
        <div className="flex-auto">
          <AgentEnquirySimpleInput
            placeholder={te('typeYourEmail')}
            {...inputProps}
          />
        </div>
        {!inline && <div className="flex-auto" />}
        <div className={clsx([inline ? 'shrink-0' : 'w-full'])}>
          <AgentEnquirySimpleButton
            onClick={handleConfirm}
            disabled={disabledButton}
          >
            {loading ? 'loading...' : te('getPriceInfo')}
          </AgentEnquirySimpleButton>
        </div>
      </div>
    </div>
  )
}
